/**
 * NMS Scroll Coordinator
 * 
 * Prevents main thread blocking by coordinating all scroll handlers
 * into a single requestAnimationFrame cycle with priority scheduling.
 * 
 * MUST load before any other scripts that add scroll listeners.
 */
(function() {
  'use strict';
  
  if (window.NMSScrollCoordinator) return; // Already loaded
  
  const scrollHandlers = new Set();
  let isProcessing = false;
  let rafId = null;
  
  // Throttle scroll processing to max 60fps
  let lastProcessTime = 0;
  const MIN_PROCESS_INTERVAL = 16; // ~60fps
  
  function processScrollHandlers() {
    const now = performance.now();
    
    // Skip if we processed recently (throttle to 60fps)
    if (now - lastProcessTime < MIN_PROCESS_INTERVAL) {
      rafId = requestAnimationFrame(processScrollHandlers);
      return;
    }
    
    lastProcessTime = now;
    isProcessing = true;
    
    // Execute all handlers in a single frame
    scrollHandlers.forEach(handler => {
      try {
        handler();
      } catch (error) {
        console.error('[ScrollCoordinator] Handler error:', error);
      }
    });
    
    isProcessing = false;
    rafId = null;
  }
  
  function onScroll() {
    if (rafId) return; // Already scheduled
    rafId = requestAnimationFrame(processScrollHandlers);
  }
  
  // Single global scroll listener
  window.addEventListener('scroll', onScroll, { passive: true });
  
  // Public API
  window.NMSScrollCoordinator = {
    /**
     * Register a scroll handler
     * @param {Function} handler - Function to call on scroll
     * @returns {Function} - Unregister function
     */
    register(handler) {
      if (typeof handler !== 'function') {
        console.error('[ScrollCoordinator] Handler must be a function');
        return () => {};
      }
      
      scrollHandlers.add(handler);
      console.log(`[ScrollCoordinator] Registered handler. Total: ${scrollHandlers.size}`);
      
      // Return unregister function
      return () => {
        scrollHandlers.delete(handler);
        console.log(`[ScrollCoordinator] Unregistered handler. Total: ${scrollHandlers.size}`);
      };
    },
    
    /**
     * Get current handler count
     */
    getHandlerCount() {
      return scrollHandlers.size;
    },
    
    /**
     * Force immediate processing (for testing)
     */
    forceProcess() {
      if (!isProcessing) {
        processScrollHandlers();
      }
    }
  };
  
  console.log('[ScrollCoordinator] Initialized - All scroll handlers will be coordinated');
})();
